import type { ConsentSnapshot } from '@/lib/consent';
import Logger from '@/lib/logger';
import {
  disableAnonymousAmplitude,
  enableAnonymousAmplitude,
  isAnonymousAmplitudeEnabled,
  optOutAnonymousAmplitudeImmediately,
  setAnonymousAmplitudeFeatureFlagEnabled,
} from './anonymousClient';
import {
  getAnonymousAmplitudeIdentity,
  peekAnonymousAmplitudeIdentity,
  resetAnonymousAmplitudeIdentity,
} from './anonymousIdentity';
import type { AmplitudeSessionReplayStorage } from './client';
import {
  disableConsentedAmplitude,
  enableConsentedAmplitude,
  isAmplitudeConfigured,
  isConsentedAmplitudeEnabled,
  optOutConsentedAmplitudeImmediately,
  prepareAmplitudeStartupRetry,
} from './client';
import { discardPendingAmplitudeItems } from './pendingEvents';

export type AmplitudeConsentMode = 'off' | 'anonymous' | 'consented';

const logger = new Logger('AmplitudeConsent');

let activeMode: AmplitudeConsentMode = 'off';
let targetMode: AmplitudeConsentMode = 'off';
let targetSessionReplayStorage: AmplitudeSessionReplayStorage = 'idb';
let targetRevision = 0;
let transitionInProgress: Promise<void> | null = null;
let shouldRotateAnonymousIdentity = false;
let failedTargetRevision: number | null = null;
let startupRetryRevision: number | null = null;

const shouldPreserveConsentedStartupRetry = (): boolean =>
  targetMode === 'consented' && startupRetryRevision === targetRevision;

export const resolveAmplitudeConsentMode = (
  { allowed, hydrated, isGdprRegion }: ConsentSnapshot,
  anonymousTrackingEnabled: boolean
): AmplitudeConsentMode => {
  if (!hydrated) {
    return 'off';
  }

  if (allowed) {
    return 'consented';
  }

  return isGdprRegion && anonymousTrackingEnabled ? 'anonymous' : 'off';
};

export const getAmplitudeConsentMode = (): AmplitudeConsentMode => activeMode;

const disableMode = async (mode: AmplitudeConsentMode): Promise<void> => {
  if (mode === 'anonymous') {
    await disableAnonymousAmplitude();
  } else if (mode === 'consented') {
    await disableConsentedAmplitude();
  }
};

const disableAllModes = async (): Promise<void> => {
  try {
    await disableAnonymousAmplitude();
  } catch (error) {
    logger.warn('[AmplitudeConsent] Failed to roll back anonymous Amplitude', error);
  }

  try {
    await disableConsentedAmplitude(shouldPreserveConsentedStartupRetry, () =>
      targetMode === 'consented' ? 'startup-retry' : 'withdrawal'
    );
  } catch (error) {
    logger.warn('[AmplitudeConsent] Failed to roll back consented Amplitude', error);
  }
};

const transitionMode = async (
  fromMode: AmplitudeConsentMode,
  requestedMode: AmplitudeConsentMode,
  requestedRevision: number,
  requestedSessionReplayStorage: AmplitudeSessionReplayStorage
): Promise<AmplitudeConsentMode> => {
  let anonymousHandoff =
    requestedMode === 'consented' && !shouldRotateAnonymousIdentity
      ? peekAnonymousAmplitudeIdentity()
      : undefined;

  if (
    requestedMode === 'consented' &&
    fromMode === 'anonymous' &&
    !shouldRotateAnonymousIdentity &&
    !anonymousHandoff
  ) {
    anonymousHandoff = getAnonymousAmplitudeIdentity();
  }

  if (fromMode === 'off' && requestedMode === 'anonymous') {
    // Clear consented SDK and replay data left by an earlier page load before anonymous tracking starts. There is no live consented mode to tear down in this transition.
    await disableConsentedAmplitude(
      () => false,
      () => (targetMode === 'off' ? 'withdrawal' : 'anonymous-handoff')
    );
  } else {
    await disableMode(fromMode);
  }

  if (
    requestedMode === 'anonymous' &&
    (fromMode === 'consented' || shouldRotateAnonymousIdentity)
  ) {
    resetAnonymousAmplitudeIdentity();
    shouldRotateAnonymousIdentity = false;
  } else if (requestedMode === 'consented' && shouldRotateAnonymousIdentity) {
    resetAnonymousAmplitudeIdentity();
    shouldRotateAnonymousIdentity = false;
  }

  if (targetMode !== requestedMode) {
    return 'off';
  }

  if (requestedMode === 'anonymous') {
    const didEnable = await enableAnonymousAmplitude(
      getAnonymousAmplitudeIdentity(),
      () => targetMode === requestedMode && targetRevision === requestedRevision
    );

    if (!didEnable || targetMode !== requestedMode || targetRevision !== requestedRevision) {
      await disableAnonymousAmplitude();
      return 'off';
    }

    if (!isAnonymousAmplitudeEnabled()) {
      throw new Error('Anonymous Amplitude did not enable');
    }
  } else if (requestedMode === 'consented') {
    const didEnable = await enableConsentedAmplitude(
      anonymousHandoff,
      () => targetMode === requestedMode && targetRevision === requestedRevision,
      requestedSessionReplayStorage
    );

    if (!didEnable) {
      await disableConsentedAmplitude();
      return 'off';
    }

    if (!isConsentedAmplitudeEnabled()) {
      throw new Error('Consented Amplitude did not enable');
    }
    shouldRotateAnonymousIdentity = false;
  }

  return requestedMode;
};

const drainTransitions = async (): Promise<number | null> => {
  while (activeMode !== targetMode) {
    const requestedMode = targetMode;
    const requestedRevision = targetRevision;
    const requestedSessionReplayStorage = targetSessionReplayStorage;
    if (startupRetryRevision === requestedRevision) {
      startupRetryRevision = null;
    }
    failedTargetRevision = null;

    try {
      activeMode = await transitionMode(
        activeMode,
        requestedMode,
        requestedRevision,
        requestedSessionReplayStorage
      );
    } catch (error) {
      failedTargetRevision = requestedRevision;
      await disableAllModes();
      activeMode = 'off';
      shouldRotateAnonymousIdentity = targetMode !== 'consented';
      logger.warn(`[AmplitudeConsent] Failed to transition to ${requestedMode}`, error);

      if (targetMode === requestedMode && targetRevision === requestedRevision) {
        return requestedRevision;
      }
    }
  }

  return null;
};

const startTransitions = (): Promise<void> => {
  if (transitionInProgress) {
    return transitionInProgress;
  }

  if (activeMode === targetMode) {
    return Promise.resolve();
  }

  let failedRevision: number | null = null;
  const transition = drainTransitions()
    .then((revision) => {
      failedRevision = revision;
    })
    .finally(() => {
      if (transitionInProgress !== transition) {
        return;
      }

      transitionInProgress = null;
      if (activeMode !== targetMode && failedRevision !== targetRevision) {
        return startTransitions();
      }
    });
  transitionInProgress = transition;
  return transition;
};

export const applyAmplitudeConsent = (
  snapshot: ConsentSnapshot,
  anonymousTrackingEnabled: boolean
): Promise<void> => {
  setAnonymousAmplitudeFeatureFlagEnabled(anonymousTrackingEnabled);

  const nextMode =
    typeof window !== 'undefined' && isAmplitudeConfigured()
      ? resolveAmplitudeConsentMode(snapshot, anonymousTrackingEnabled)
      : 'off';
  const nextSessionReplayStorage: AmplitudeSessionReplayStorage = snapshot.isGdprRegion
    ? 'memory'
    : 'idb';

  if (nextMode === 'off') {
    discardPendingAmplitudeItems();
  } else if (nextMode === 'anonymous') {
    discardPendingAmplitudeItems(({ adTrackingConsent }) => adTrackingConsent);
  }

  if (nextMode !== 'anonymous') {
    optOutAnonymousAmplitudeImmediately();
  }

  if (nextMode !== 'consented') {
    optOutConsentedAmplitudeImmediately();
  } else {
    prepareAmplitudeStartupRetry();
  }

  const shouldRetryFailedStartup =
    nextMode === 'consented' &&
    activeMode !== targetMode &&
    (failedTargetRevision === targetRevision ||
      (transitionInProgress !== null && failedTargetRevision !== null));

  if (
    !shouldRetryFailedStartup &&
    nextMode === targetMode &&
    (transitionInProgress || activeMode === targetMode)
  ) {
    return transitionInProgress ?? Promise.resolve();
  }

  if (shouldRetryFailedStartup) {
    targetMode = nextMode;
    targetRevision += 1;
    startupRetryRevision = targetRevision;
    return startTransitions();
  }

  if (!transitionInProgress) {
    failedTargetRevision = null;
  }
  startupRetryRevision = null;

  if (nextMode === 'anonymous' && (activeMode === 'consented' || targetMode === 'consented')) {
    shouldRotateAnonymousIdentity = true;
  }

  targetMode = nextMode;
  targetSessionReplayStorage = nextSessionReplayStorage;
  targetRevision += 1;

  return startTransitions();
};
